import type { ChartRow, ChartSeries, ChartSpec } from './types';
import { chartTimeMilliseconds } from './timeRange';

type LegacyPoint = {
  time?: string | number;
  value?: number;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  detailText?: string;
};

type LegacySeries = {
  name?: string;
  type?: string;
  panel?: string;
  unit?: string;
  color?: string;
  markerShape?: 'arrowUp' | 'arrowDown' | 'circle' | 'square';
  markerPlacement?: 'above' | 'below' | 'at';
  compactMarker?: boolean;
  points?: LegacyPoint[];
};

function stableId(prefix: string, index: number): string {
  return `${prefix}_${index + 1}`;
}

export function nearestChartTime(
  rawTime: string | number,
  chartTimes: Array<string | number>,
): string | number {
  const target = chartTimeMilliseconds(rawTime);
  const candidates = chartTimes
    .map((value) => ({ value, millis: chartTimeMilliseconds(value) }))
    .filter((candidate): candidate is { value: string | number; millis: number } => candidate.millis !== null)
    .sort((left, right) => left.millis - right.millis);
  if (target === null || candidates.length === 0) return rawTime;

  const nearest = candidates.reduce((best, candidate) => (
    Math.abs(candidate.millis - target) < Math.abs(best.millis - target)
      ? candidate
      : best
  ));
  const intervals = candidates
    .slice(1)
    .map((candidate, index) => candidate.millis - candidates[index].millis)
    .filter((interval) => interval > 0)
    .sort((left, right) => left - right);
  const medianInterval = intervals.length > 0
    ? intervals[Math.floor(intervals.length / 2)]
    : 0;
  const maximumDistance = Math.max(5 * 60 * 1000, medianInterval);
  return Math.abs(nearest.millis - target) <= maximumDistance
    ? nearest.value
    : rawTime;
}

export function legacyIndicatorDataToChartSpec(
  indicatorData: LegacySeries[],
  signalData: LegacySeries[] = [],
): ChartSpec | null {
  if (!Array.isArray(indicatorData) || indicatorData.length === 0) return null;

  const rowsByTime = new Map<string | number, ChartRow>();
  const paneIds = new Map<string, string>();
  const series: ChartSeries[] = [];
  const primaryPanelName = indicatorData.find((item) => (
    (item.type === 'ohlc' || item.type === 'candlestick') && item.panel
  ))?.panel || indicatorData.find((item) => item.panel)?.panel;
  const panelIdByName = new Map<string, string>();

  const resolvePane = (panelName?: string): { id: string; label?: string } => {
    if (!panelName || panelName === primaryPanelName) {
      return { id: 'primary_pane', label: panelName };
    }
    const existing = panelIdByName.get(panelName);
    if (existing) return { id: existing, label: panelName };
    const id = `indicator_pane_${panelIdByName.size + 1}`;
    panelIdByName.set(panelName, id);
    return { id, label: panelName };
  };

  indicatorData.forEach((item, index) => {
    const points = Array.isArray(item.points) ? item.points : [];
    const id = stableId('series', index);
    const pane = resolvePane(item.panel);
    const paneId = pane.id;
    paneIds.set(paneId, pane.label || paneId);
    const type = item.type;
    const isCandlestick = type === 'ohlc' || type === 'candlestick';
    const dataKey = `value_${index + 1}`;
    const fieldMapping = {
      open: `open_${index + 1}`,
      high: `high_${index + 1}`,
      low: `low_${index + 1}`,
      close: `close_${index + 1}`,
    };

    points.forEach((point) => {
      if (point.time === undefined || point.time === null) return;
      const row = rowsByTime.get(point.time) || { time: point.time };
      if (isCandlestick) {
        row[fieldMapping.open] = Number(point.open);
        row[fieldMapping.high] = Number(point.high);
        row[fieldMapping.low] = Number(point.low);
        row[fieldMapping.close] = Number(point.close);
      } else {
        row[dataKey] = Number(point.value);
      }
      rowsByTime.set(point.time, row);
    });

    series.push({
      id,
      label: item.name || `Series ${index + 1}`,
      mark: isCandlestick ? 'candlestick' : type === 'bar' ? 'bar' : 'line',
      paneId,
      axisId: `${paneId}_axis`,
      dataKey: isCandlestick ? undefined : dataKey,
      fieldMapping: isCandlestick ? fieldMapping : undefined,
      style: {},
      unit: item.unit || 'value',
      frequency: 'provided',
      timezone: 'UTC',
      sourceIds: ['backtest-data'],
      showLastValue: true,
    });
  });

  const data = [...rowsByTime.values()].sort((left, right) => {
    const leftTime = chartTimeMilliseconds(left.time);
    const rightTime = chartTimeMilliseconds(right.time);
    if (leftTime !== null && rightTime !== null) {
      return leftTime - rightTime;
    }
    return String(left.time).localeCompare(String(right.time));
  });
  const panes = [...paneIds].map(([id, label]) => ({
    id,
    label: label === id ? undefined : label,
    weight: id === 'primary_pane' ? 4 : 1,
    axisIds: [`${id}_axis`],
  }));
  const chartTimes = data
    .map((row) => row.time)
    .filter((time): time is string | number => time !== undefined && time !== null);
  const markers = signalData.flatMap((signal, signalIndex) =>
    (signal.points || [])
      .filter((point) => point.time !== undefined && point.time !== null)
      .map((point, pointIndex) => ({
        id: `signal_${signalIndex + 1}_${pointIndex + 1}`,
        paneId: series[0].paneId,
        seriesId: series[0].id,
        x: nearestChartTime(point.time as string | number, chartTimes),
        value:
          typeof point.value === 'number'
            ? point.value
            : typeof point.close === 'number'
              ? point.close
              : undefined,
        placement: signal.markerPlacement || 'at',
        shape: signal.markerShape || 'circle',
        color: signal.color || '#F59E0B',
        label: signal.name || 'Signal',
        detail: point.detailText,
        compact: signal.compactMarker,
      })),
  );

  return {
    kind: 'timeseries',
    title: 'Strategy indicators',
    xAxis: { key: 'time', type: 'time' },
    axes: panes.map((pane) => ({
      id: pane.axisIds[0],
      side: 'right',
      format: 'number',
    })),
    panes,
    series,
    data,
    referenceLines: [],
    markers,
  };
}
